import { Divider, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AccessPermission } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ReportGroup } from "../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../context/useClientContext";
import GlobalDndProvider from "../../../../common/GlobalDndProvider.tsx";
import CustomDragLayer from "./CustomDragLayer";
import GroupItem from "./GroupItem";
import type { GroupType } from "./GroupType";
import GroupActions from "./actions/GroupActions";
import { useGroupsContext } from "./contexts/GroupsContext.ts";

interface Props {
  permissions: AccessPermission[];
  type: GroupType;
}
export const GroupsGrid = ({ permissions, type }: Props) => {
  const { clientCode } = useClientContext();
  const { groups, actions } = useGroupsContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [currentGroupId, setCurrentGroupId] = useState<string>();
  const [actionMenuRef, setActionMenuRef] = useState<HTMLElement | null>(null);
  const [sortedGroups, setSortedGroups] = useState<ReportGroup[]>([]);

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSortedGroups([...defined(groups[clientCode])].sort((l, r) => Number(l.order - r.order)));
  }, [clientCode, groups]);

  const sortedGroupsRef = useRef(sortedGroups);
  useEffect(() => {
    sortedGroupsRef.current = sortedGroups;
  }, [sortedGroups]);

  const onDragMove = useCallback((item: ReportGroup, replaceItem: ReportGroup) => {
    const values = [...sortedGroupsRef.current];
    const fieldIndex = values.findIndex((v) => v.id === item.id);
    const toIndex = values.findIndex((v) => v.id === replaceItem.id);
    if (fieldIndex > -1) {
      values.splice(fieldIndex, 1);
      values.splice(toIndex, 0, item);
      setSortedGroups(values);
    }
  }, []);

  const onEndMoveItem = useCallback(() => {
    const sorted = sortedGroupsRef.current.map((value, index) => {
      return { ...value, order: index + 1 } as ReportGroup;
    });
    actions
      .reorder(clientCode, sorted)
      .then(() => sendNotification("New group order has been saved."))
      .catch(() => sendNotificationError("An error occurred while reordering the group."));
  }, [actions, clientCode, sendNotification, sendNotificationError]);

  const onShowMenu = useCallback((anchorEl: HTMLElement | null, groupId: string) => {
    setCurrentGroupId(groupId);
    setActionMenuRef(anchorEl);
  }, []);

  return (
    <GlobalDndProvider>
      <Stack sx={{ flex: 1, width: "100%" }}>
        <Divider />
        {sortedGroups.map((rg) => {
          return (
            <GroupItem
              key={rg.id}
              level={rg}
              onShowMenu={onShowMenu}
              onDragMove={onDragMove}
              onEndMoveItem={onEndMoveItem}
              permissions={permissions}
            />
          );
        })}
      </Stack>
      {actionMenuRef && currentGroupId && (
        <GroupActions
          anchorEl={actionMenuRef}
          currentGroupId={currentGroupId}
          showRename
          showDelete
          onClose={() => setActionMenuRef(null)}
          type={type}
        />
      )}
      <CustomDragLayer />
    </GlobalDndProvider>
  );
};

export default GroupsGrid;
